import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { ArrowLeft, Pause, Play, Square } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { formatDuration } from '@/lib/utils/time';
import { useEncounterSession } from './EncounterSessionProvider';
import { useEncounterSessionStore } from '@/lib/stores/encounter-session-store';

export function FloatingRecorderPill() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pauseRecording, resumeRecording, stopRecording } = useEncounterSession();
  const { recordingMeta, originRoute } = useEncounterSessionStore();
  const [elapsed, setElapsed] = useState(0);

  const isActiveRecording =
    recordingMeta.status === 'recording' || recordingMeta.status === 'paused' || recordingMeta.status === 'interrupted';
  const isOnOriginRoute = originRoute && location.pathname === new URL(originRoute, window.location.origin).pathname;

  useEffect(() => {
    if (recordingMeta.status === 'recording' && recordingMeta.startedAt) {
      const tick = () => {
        setElapsed(Date.now() - recordingMeta.startedAt! - recordingMeta.pausedDuration);
      };
      tick();
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }

    // Paused or interrupted — timer frozen at last-known elapsed.
    if ((recordingMeta.status === 'paused' || recordingMeta.status === 'interrupted') && recordingMeta.startedAt) {
      setElapsed(Date.now() - recordingMeta.startedAt - recordingMeta.pausedDuration);
    }

    if (recordingMeta.status === 'idle' || recordingMeta.status === 'stopped') {
      setElapsed(0);
    }
  }, [recordingMeta.status, recordingMeta.startedAt, recordingMeta.pausedDuration]);

  if (!isActiveRecording || isOnOriginRoute) {
    return null;
  }

  const handleNavigateBack = () => {
    if (originRoute) {
      navigate({ to: originRoute });
    }
  };

  const handleStop = async () => {
    await stopRecording();
    if (originRoute) {
      navigate({ to: originRoute });
    }
  };

  const isPaused = recordingMeta.status === 'paused';
  const isInterrupted = recordingMeta.status === 'interrupted';
  const ariaLabel = isInterrupted
    ? 'Recording paused by phone call — will resume automatically'
    : isPaused
      ? 'Recording paused'
      : 'Recording in progress';

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      className={cn(
        'fixed bottom-6 right-6 z-50 flex items-center gap-3',
        'rounded-full border-2 bg-background/95 px-4 py-2.5 shadow-lg backdrop-blur-sm',
        'cursor-pointer select-none',
        'pb-[calc(0.625rem+env(safe-area-inset-bottom,0px))]',
        isPaused || isInterrupted ? 'border-warning' : 'border-destructive',
      )}
      onClick={handleNavigateBack}>
      {/* Desktop: "Back to Encounter" label */}
      <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
        <ArrowLeft className="h-3 w-3" />
        <span>Back to Encounter</span>
      </div>

      <div className="hidden md:block h-4 w-px bg-border" />

      {/* Recording indicator + timer */}
      <div
        className={cn(
          'h-3 w-3 shrink-0 rounded-full',
          isInterrupted ? 'bg-warning animate-pulse' : isPaused ? 'bg-warning' : 'bg-destructive animate-pulse',
        )}
      />

      <span className="text-sm font-semibold tabular-nums">{formatDuration(elapsed)}</span>

      <div className="h-4 w-px bg-border" />

      {/* Controls */}
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Button
          size="icon"
          type="button"
          variant="ghost"
          className="h-11 w-11"
          aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
          onClick={isPaused ? resumeRecording : pauseRecording}>
          {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
        </Button>
        <Button
          size="icon"
          type="button"
          variant="ghost"
          className="h-11 w-11 text-destructive hover:text-destructive"
          aria-label="Stop recording"
          onClick={handleStop}>
          <Square className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
